import { useState } from "react";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { fmtNum, dateLabel, effectiveBalance } from "@/lib/format";

// Map raw currency_type strings from extension recipes (Amex sends
// "POINTS"/"MILES", others may add their own) to short display labels.
// Unknown values pass through lowercased so the UI still says something.
function currencyLabel(currencyType) {
  if (!currencyType) return "";
  const t = String(currencyType).trim().toUpperCase();
  if (t === "POINTS") return "pts";
  if (t === "MILES")  return "miles";
  return t.toLowerCase();
}
import { useReachable, ReachableInfoButton, PartnersModal } from "@/components/Reachable";

const Logo = ({ p }) => (
  <div
    className="w-14 h-14 rounded-[12px] flex items-center justify-center text-white font-bold text-[16px] shrink-0 overflow-hidden"
    style={{ background: p.color || "#1f2937" }}
  >
    {p.logo ? (
      <img
        src={p.logo}
        alt=""
        className="w-full h-full object-contain bg-white"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    ) : (
      p.name.charAt(0)
    )}
  </div>
);

const isExpiringSoon = (iso) => {
  if (!iso || iso === "no expiry") return false;
  try {
    const d = new Date(iso);
    const days = (d.getTime() - Date.now()) / 86400000;
    return days >= 0 && days <= 90;
  } catch {
    return false;
  }
};

const isExpired = (iso) => {
  if (!iso || iso === "no expiry") return false;
  try {
    return new Date(iso).getTime() < Date.now();
  } catch {
    return false;
  }
};

export default function ProgramCards({ programs, awards = [], onEdit, onAddAward, onDeleteAward }) {
  const [adding, setAdding] = useState(null); // program object when adding
  const [form, setForm] = useState({ label: "", award_type: "", expires_at: "", notes: "" });

  const { showReachable, reachable } = useReachable();
  const [partnersModal, setPartnersModal] = useState(null);

  const submitAward = async (e) => {
    e?.preventDefault();
    if (!adding) return;
    await onAddAward({ ...form, program_id: adding.id });
    setForm({ label: "", award_type: "", expires_at: "", notes: "" });
    setAdding(null);
  };

  if (!programs.length) return null;

  return (
    <section data-testid="program-cards">
      <div className="section-eyebrow mb-3">Program details</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {programs.map((p) => {
          const programAwards = awards.filter((a) => a.program_id === p.id);
          return (
            <div
              key={p.id}
              className="card p-5 relative"
              data-testid={`program-card-${p.code}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <Logo p={p} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => onEdit(p)}
                      className="text-left min-w-0 flex-1"
                    >
                      <div className="font-display font-bold text-[15px] tracking-tight truncate">
                        {p.name}
                        <span className="text-neutral-500 font-normal"> ({p.holder || "self"})</span>
                      </div>
                    </button>
                    {showReachable && (
                      <ReachableInfoButton
                        program={p}
                        reach={reachable[p.id]}
                        onOpenMore={(payload) => setPartnersModal(payload)}
                      />
                    )}
                  </div>
                </div>
                <button
                  onClick={() => onEdit(p)}
                  className="text-neutral-300 hover:text-black"
                  aria-label="Edit balance"
                  data-testid={`edit-pencil-${p.code}`}
                >
                  <Pencil size={14} />
                </button>
              </div>

              {Array.isArray(p.balances) && p.balances.length > 1 ? (
                <>
                  <div className="section-eyebrow">
                    {p.balances.length} programs
                  </div>
                  <div className="mt-1.5 space-y-2">
                    {p.balances.map((b, i) => (
                      <div key={i} className="leading-tight">
                        <div className="flex items-baseline gap-2">
                          <span className="font-display tabular text-[22px] font-extrabold tracking-tight">
                            {fmtNum(b.balance)}
                          </span>
                          {b.currency_type && (
                            <span className="text-[11px] text-neutral-500 font-semibold uppercase tracking-wide">
                              {currencyLabel(b.currency_type)}
                            </span>
                          )}
                        </div>
                        {(b.tier || b.program_name) && (
                          <div className="text-[11.5px] text-neutral-600 mt-0.5">
                            {b.tier || b.program_name}
                          </div>
                        )}
                        {b.member_id && (
                          <div className="font-mono text-[10.5px] text-neutral-400">#{b.member_id}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="section-eyebrow">
                    {p.pooled_balance > 0 ? "Pooled" : (p.currency === "mi" ? "Miles" : "Points")} balance
                  </div>
                  <div className="font-display tabular text-[28px] font-extrabold tracking-tight mt-0.5">
                    {fmtNum(effectiveBalance(p))}
                  </div>

                  {p.tier && (
                    <div className="mt-2">
                      <span className="pill pill-neutral">{p.tier}</span>
                    </div>
                  )}

                  {/* Per-program details surfaced by the ReservoirAI extension
                      (United / Delta / etc.): PQP / PQF for elite progress,
                      lifetime miles, member id. Each is hidden when not set. */}
                  {(p.pqp != null || p.pqf != null || p.lifetime_miles != null || p.member_id) && (
                    <div className="mt-3 text-[11.5px] text-neutral-600 leading-snug space-y-0.5">
                      {(p.pqp != null || p.pqf != null) && (
                        <div className="tabular">
                          {p.pqp != null && <>PQP <span className="font-semibold text-neutral-800">{fmtNum(p.pqp)}</span></>}
                          {p.pqp != null && p.pqf != null && <span className="mx-1.5 text-neutral-400">·</span>}
                          {p.pqf != null && <>PQF <span className="font-semibold text-neutral-800">{fmtNum(p.pqf)}</span></>}
                        </div>
                      )}
                      {p.lifetime_miles != null && (
                        <div className="tabular">
                          Lifetime <span className="font-semibold text-neutral-800">{fmtNum(p.lifetime_miles)}</span>{" "}
                          {p.currency === "mi" ? "miles" : "points"}
                        </div>
                      )}
                      {p.member_id && (
                        <div className="font-mono text-[11px] text-neutral-500">#{p.member_id}</div>
                      )}
                    </div>
                  )}
                </>
              )}

              {p.notes && (
                <div
                  className="mt-3 p-3 text-[13px] leading-relaxed text-neutral-700 bg-[#f7faf2] border-l-2 rounded-r-[8px]"
                  style={{ borderLeftColor: "#166534" }}
                >
                  {p.notes}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-black/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="section-eyebrow">Awards & certs</div>
                  <button
                    onClick={() => setAdding(p)}
                    className="text-[11px] font-semibold text-neutral-500 hover:text-black inline-flex items-center gap-1"
                    data-testid={`add-award-${p.code}`}
                  >
                    <Plus size={11} /> Add
                  </button>
                </div>
                {programAwards.length === 0 ? (
                  <div className="text-[12px] text-neutral-400">
                    No certs on file.
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {programAwards.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center gap-2 text-[12.5px] group/aw"
                      >
                        <span className="flex-1 truncate">
                          <span className="font-medium">{a.label}</span>
                          {a.award_type && (
                            <span className="text-neutral-400"> · {a.award_type}</span>
                          )}
                        </span>
                        <span
                          className={`tabular shrink-0 ${
                            isExpired(a.expires_at)
                              ? "text-red-700"
                              : isExpiringSoon(a.expires_at)
                              ? "text-amber-700 font-semibold"
                              : "text-neutral-500"
                          }`}
                        >
                          {dateLabel(a.expires_at)}
                        </span>
                        <button
                          onClick={() => onDeleteAward(a.id)}
                          className="opacity-0 group-hover/aw:opacity-100 transition-opacity text-neutral-400 hover:text-red-700"
                          aria-label="Delete award"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {partnersModal && (
        <PartnersModal payload={partnersModal} onClose={() => setPartnersModal(null)} />
      )}

      {adding && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 fade-in"
          style={{ background: "rgba(10,10,10,0.45)" }}
          onClick={() => setAdding(null)}
          data-testid="add-award-modal"
        >
          <form
            className="card w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitAward}
          >
            <button
              type="button"
              onClick={() => setAdding(null)}
              className="absolute top-3 right-3 text-neutral-400 hover:text-black"
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <div className="font-display font-bold text-lg tracking-tight">
              Add award · {adding.name}
            </div>
            <p className="text-[13px] text-neutral-600 mt-1 mb-4">
              Free-night certs, suite upgrades, Guest of Honor, etc.
            </p>
            <div className="space-y-3">
              <input
                required
                autoFocus
                className="input"
                placeholder="Label (e.g. Cat 1–4 free night)"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid="award-label-input"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  className="input"
                  placeholder="Type (Milestone, Globalist, Annual…)"
                  value={form.award_type}
                  onChange={(e) => setForm({ ...form, award_type: e.target.value })}
                />
                <input
                  type="date"
                  className="input"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                />
              </div>
              <input
                className="input"
                placeholder="Notes (optional)"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                className="btn btn-ghost text-[13px]"
                onClick={() => setAdding(null)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary text-[13px]"
                data-testid="award-save-btn"
              >
                Save award
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

